import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Plug, Upload, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip } from "@/components/Bits";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Integrations() {
  const [connectors, setConnectors] = useState([]);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/integrations/connectors").then((r) => setConnectors(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const sync = async (key, name) => {
    setBusy(key);
    try { const r = await api.post(`/integrations/${key}/sync`); toast.success(`${name}: ${r.data.created} imported, ${r.data.skipped} skipped (already synced)`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const toggle = async (key) => {
    try { await api.post(`/integrations/${key}/toggle`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { const r = await api.post("/integrations/import-orders", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`CSV: ${r.data.created} orders imported, ${r.data.skipped} skipped`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div>
      <PageHeader title="Order Sync" subtitle="Import orders from marketplaces, social and your website"
        actions={<>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onUpload} className="hidden" data-testid="orders-csv-input" />
          <button data-testid="import-orders-button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 text-sm border border-white/15 text-zinc-300 hover:bg-white/5 px-4 py-2 rounded-sm transition-colors"><Upload size={15} /> Import CSV</button>
        </>} />
      <div className="p-8 space-y-6">
        <div className="bg-[#FFD600]/5 border border-[#FFD600]/20 rounded-sm px-4 py-3 text-xs text-[#FFD600] font-mono flex items-center gap-2">
          <Plug size={14} /> Connectors run in MOCK mode — "Sync now" pulls sample orders. Connect real seller accounts later with API credentials.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {connectors.map((c) => (
            <div key={c.key} data-testid={`connector-${c.key}`} className="bg-[#121212] border border-white/10 rounded-sm p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center font-display font-bold text-sm" style={{ backgroundColor: `${c.color}1a`, color: c.color, border: `1px solid ${c.color}55` }}>{c.name.charAt(0)}</div>
                  <div>
                    <h3 className="font-display text-lg">{c.name}</h3>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{c.kind} · MOCK</p>
                  </div>
                </div>
                <Chip className="bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]">MOCKED</Chip>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs">
                <button data-testid={`connector-toggle-${c.key}`} onClick={() => toggle(c.key)} className="flex items-center gap-1.5 font-mono text-zinc-400 hover:text-white transition-colors">
                  {c.connected ? <CheckCircle2 size={14} className="text-[#00FF66]" /> : <Circle size={14} className="text-zinc-600" />}
                  {c.connected ? "Connected" : "Disconnected"}
                </button>
                <span className="font-mono text-zinc-600">{c.imported_orders} orders · {c.last_sync ? `synced ${c.last_sync.slice(0, 10)}` : "never synced"}</span>
              </div>
              <button data-testid={`connector-sync-${c.key}`} disabled={busy === c.key} onClick={() => sync(c.key, c.name)}
                className="mt-3 w-full flex items-center justify-center gap-2 text-sm bg-white text-black font-medium py-2 rounded-sm hover:bg-zinc-200 transition-colors disabled:opacity-60">
                <RefreshCw size={14} className={busy === c.key ? "animate-spin" : ""} /> {busy === c.key ? "Syncing…" : "Sync now"}
              </button>
            </div>
          ))}
        </div>
        <div className="text-[11px] font-mono text-zinc-600 leading-relaxed">
          CSV columns: external_order_id, customer_name, delivery_address, sku, name, qty, unit_price, mrp (one row per line item; rows with the same external_order_id merge into one order). Imports dedupe by external_order_id.
        </div>
      </div>
    </div>
  );
}
